import { exportMetrics } from "../shared/geometry";
import type { Sheet } from "../shared/schema";
import { sheetPageDimensionsMm } from "../shared/sheets";
import type { PDFFont } from "pdf-lib";

/** Keep a source drawing's scale statement separate from its artwork. */
export function drawingExportLayout(
  sheet: Pick<Sheet, "props" | "meta">,
  paper: "A3" | "A4",
  scale: number,
  landscape: boolean,
) {
  const { w, h, metersPerUnit, source } = sheet.props;
  const { widthMm: pageW, heightMm: pageH } = sheetPageDimensionsMm(
    sheet,
    paper,
    landscape,
  );
  const sourceMargin = source ? 12 : 0;
  const mmPerUnit = metersPerUnit
    ? (metersPerUnit * 1000) / scale
    : Math.min((pageW - (source ? 20 : 0)) / w, (pageH - sourceMargin * 2) / h);
  const metrics =
    (sheet.props.paper === "A3" || sheet.props.paper === "A4") && metersPerUnit
      ? exportMetrics(w, h, metersPerUnit, scale, paper, landscape, !source)
      : {
          pageW,
          pageH,
          mmPerUnit,
          drawingW: w * mmPerUnit,
          drawingH: h * mmPerUnit,
          fits:
            w * mmPerUnit <= pageW - (source ? 20 : 0) + 0.001 &&
            h * mmPerUnit <= pageH - sourceMargin * 2 + 0.001,
        };
  return {
    ...metrics,
    topMarginMm: sourceMargin,
    scaleLabel: metersPerUnit ? `1:${scale}` : "Not to Scale - fit to page",
  };
}

/** Preserve scale information when a long source name exceeds the paper width. */
export function drawingExportFooter(
  sheet: Pick<Sheet, "props">,
  metrics: Pick<
    ReturnType<typeof drawingExportLayout>,
    "pageW" | "pageH" | "scaleLabel"
  >,
  font: Pick<PDFFont, "widthOfTextAtSize">,
) {
  const ascii = (text: string) => text.replace(/[^\x20-\x7E]/g, "");
  const details = ascii(
    `${Math.round(metrics.pageW)} x ${Math.round(metrics.pageH)} mm   ${metrics.scaleLabel}   |   REV ${sheet.props.revision}   |   Print at 100%`,
  );
  const width = ((metrics.pageW - 20) * 72) / 25.4;
  const size = Math.min(8, width / font.widthOfTextAtSize(details, 1));
  let name = ascii(sheet.props.name.slice(0, 60));
  let shortened = sheet.props.name.length > 60;
  for (;;) {
    const text = name
      ? `${name}${shortened ? "..." : ""}   |   ${details}`
      : details;
    if (font.widthOfTextAtSize(text, size) <= width + 1e-6)
      return { text, size };
    name = name.slice(0, -1);
    shortened = true;
  }
}
